import { useCallback, useEffect, useRef } from "react";
import { useFetchClubPaymentSettingsQuery } from "@/api/clubsApi";
import { SidePanelBody } from "@/components/ui/SidePanel";
import { Skeleton } from "@/components/ui/Skeleton";
import { Icon } from "@/components/ui/Icon";
import { supabase } from "@/lib/supabase";
import { StripeConnectPanel } from "./StripeConnectPanel";
import { ClubPaymentSettingsForm } from "./ClubPaymentSettingsForm";

interface SubscriptionsPaymentsViewProps {
  clubId: string;
}

export function SubscriptionsPaymentsView({
  clubId,
}: SubscriptionsPaymentsViewProps) {
  const { data, isLoading, refetch } = useFetchClubPaymentSettingsQuery(clubId);
  const hasHandledConnect = useRef(false);

  const refreshStripeData = useCallback(async () => {
    if (!clubId) return;
    try {
      await supabase.functions.invoke("get-club-stripe-status", {
        body: { clubId },
      });
      refetch();
    } catch (error) {
      console.error("Error refreshing Stripe data:", error);
    }
  }, [clubId, refetch]);

  // Handle Stripe Connect return URL params
  useEffect(() => {
    if (hasHandledConnect.current) return;

    const params = new URLSearchParams(window.location.search);
    const connectParam = params.get("connect");

    if (connectParam === "success" || connectParam === "refresh") {
      hasHandledConnect.current = true;
      refreshStripeData().then(() => {
        params.delete("connect");
        const newSearch = params.toString();
        const newUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : "");
        window.history.replaceState({}, "", newUrl);
      });
    }
  }, [refreshStripeData]);

  // Poll Stripe status when account is pending or incomplete with details submitted
  useEffect(() => {
    const shouldPoll =
      data?.stripe_account_id &&
      (data.stripe_account_status === "pending" ||
        (data.stripe_account_status === "incomplete" && data.stripe_account_details_submitted === true));

    if (!shouldPoll) return;

    const interval = setInterval(() => {
      refreshStripeData();
    }, 10000);

    return () => clearInterval(interval);
  }, [
    data?.stripe_account_id,
    data?.stripe_account_status,
    data?.stripe_account_details_submitted,
    refreshStripeData,
  ]);

  const handleViewDashboard = useCallback(async () => {
    try {
      const response = await supabase.functions.invoke(
        "create-stripe-login-link",
        {
          body: { clubId },
        }
      );
      if (response.data?.url) {
        window.open(response.data.url, "_blank");
      }
    } catch (error) {
      console.error("Error creating Stripe login link:", error);
    }
  }, [clubId]);

  return (
    <SidePanelBody>
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-60 w-full rounded-2xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <StripeConnectPanel
            clubId={clubId}
            paymentSettings={data ?? null}
            onRefresh={refreshStripeData}
          />

          {data?.stripe_account_id && (
            <div className="flex justify-center">
              <button
                onClick={handleViewDashboard}
                className="flex items-center gap-1.5 text-body-2 text-text-secondary hover:text-text-primary transition-colors py-2"
              >
                <Icon
                  name="arrow-up-right-from-square"
                  size={14}
                  className="text-text-secondary"
                />
                View subscription dashboard
              </button>
            </div>
          )}

          <ClubPaymentSettingsForm
            clubId={clubId}
            paymentSettings={data}
            onRefresh={refreshStripeData}
          />
        </div>
      )}
    </SidePanelBody>
  );
}
